import React, { useState, useEffect } from "react";
import { LivePal, GearPal, GearItem } from "../../types/Pal";
import useCategoryTranslation from "../../hooks/useCategoryTranslation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteLivepal } from "../../backend/api/livepal";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { deleteGearpal, deleteGearItem } from "../../backend/api/gearlpal";
import PalBookingView from "./PalBookingView";
import PalPopupContent from "./PalPopupContent";
import GearItemDetail from "./GearItemDetail";
import BackButton from "../BackButton";
import CloseButton from "../CloseButton";
import { useUserStore } from "../../stores/UserStore";
import AddOrEditPalForm from "../AddOrEditPalForm/AddOrEditPalForm";
import EditGearItemModal from "../EditGearItemModal";
import AddGearItemModal from "../AddGearItemModal";
import {
  IoTrashOutline,
  IoPencilOutline,
  IoLocationOutline,
} from "react-icons/io5";

interface PalPopupProps {
  pal: LivePal | GearPal;
  onEditItems?: () => void;
  onClose: () => void;
  onBack?: () => void;
  zIndex?: number;
  initialSelectedItem?: GearItem | null;
}

const PalPopup: React.FC<PalPopupProps> = ({
  pal,
  onEditItems,
  onClose,
  onBack,
  zIndex = 1001,
  initialSelectedItem = null,
}) => {
  const { t } = useCategoryTranslation();
  const { i18n } = useTranslation();
  const { user } = useUserStore();

  const [currentPal, setCurrentPal] = useState<LivePal | GearPal>(pal);
  const [selectedItem, setSelectedItem] = useState<GearItem | null>(
    initialSelectedItem
  );
  const [isBooking, setIsBooking] = useState(false);
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState<GearItem | null>(null);
  const [isAddingItem, setIsAddingItem] = useState(false);

  useEffect(() => {
    setCurrentPal(pal);
  }, [pal]);

  const isOwner =
    user && user.email.toLowerCase() === currentPal.userEmail.toLowerCase();

  const { mutate: deleteLivepalMutation } = useMutation({
    mutationFn: (id: number) => deleteLivepal(id, i18n.language),
    onSuccess: () => {
      toast.success(t("pal.deleted", "Pal eliminata con successo"));
      queryClient.invalidateQueries({ queryKey: ["livepals"] });
      queryClient.invalidateQueries({ queryKey: ["my-livepals"] });
      onClose();
    },
    onError: () => {
      toast.error(t("pal.deleteFailed"));
    },
  });

  const { mutate: deleteGearpalMutation } = useMutation({
    mutationFn: (id: number) => deleteGearpal(id, i18n.language),
    onSuccess: () => {
      toast.success(
        t("pal.gearDeleted", "Attrezzatura eliminata con successo")
      );
      queryClient.invalidateQueries({ queryKey: ["gearpals"] });
      queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
      onClose();
    },
    onError: () => {
      toast.error(t("pal.deleteFailed"));
    },
  });

  const { mutate: deleteItemMutation } = useMutation({
    mutationFn: (id: number) => deleteGearItem(id, i18n.language),
    onSuccess: () => {
      toast.success(t("item.deleted", "Articolo eliminato con successo"));
      queryClient.invalidateQueries({ queryKey: ["gearpals"] });
      queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
      if (selectedItem) setSelectedItem(null);
    },
    onError: () => {
      toast.error(
        t("item.deleteFailed", "Errore nell'eliminazione dell'articolo")
      );
    },
  });

  const handleBook = async (date: Date) => {
    console.log("Booking for:", date);
    toast.success(
      t("pal.bookingComingSoon", "Prenotazione disponibile prossimamente!")
    );
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const onEditRequest = () => {
    setIsEditing(true);
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 transition-all duration-300"
      style={{ zIndex }}
      onClick={handleOverlayClick}
    >
      <div className="bg-[#2D1B4EE5] border-modal rounded-2xl shadow-2xl max-w-[512px] w-full max-h-[85vh] overflow-hidden flex flex-col relative text-white">
        {/* Header Section */}
        <div className="p-5 sm:p-7 border-b border-white/5 bg-[#2C1A4A]/50 shrink-0">
          <div className="flex justify-between items-start gap-4">
            <div className="flex items-center space-x-4 min-w-0 flex-1">
              {(onBack || selectedItem || isBooking) && (
                <BackButton
                  onClick={
                    selectedItem
                      ? () => {
                          if (isBooking) setIsBooking(false);
                          else setSelectedItem(null);
                        }
                      : onBack
                  }
                />
              )}
              <div className="min-w-0 flex-1">
                {!selectedItem && !isBooking && (
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        currentPal.type === "LivePal"
                          ? "bg-secondary ring-4 ring-secondary/20"
                          : "bg-primary ring-4 ring-primary/20"
                      }`}
                    />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">
                      {currentPal.type}
                    </span>
                  </div>
                )}
                <h2 className="text-xl sm:text-2xl font-bold truncate pr-2 text-white tracking-tight">
                  {selectedItem
                    ? selectedItem.name
                    : isBooking
                    ? currentPal.name
                    : currentPal.name}
                </h2>
                {!selectedItem && !isBooking && (
                  <div className="flex items-center gap-1.5 text-white/40 mt-1">
                    <IoLocationOutline size={14} className="text-secondary" />
                    <span className="text-xs font-medium">
                      {currentPal.city}, {currentPal.region}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 shrink-0">
              <div className="ml-2">
                <CloseButton onClick={onClose} />
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-grow overflow-y-auto custom-scrollbar p-5 sm:p-8">
          {selectedItem && currentPal.type === "GearPal" ? (
            isBooking ? (
              <PalBookingView
                title={selectedItem.name}
                onBook={handleBook}
                onBack={() => setIsBooking(false)}
              />
            ) : (
              <GearItemDetail
                item={selectedItem}
                pal={currentPal as GearPal}
                onBack={() => setSelectedItem(null)}
              />
            )
          ) : isBooking && currentPal.type === "LivePal" ? (
            <PalBookingView
              title={currentPal.name}
              onBook={handleBook}
              onBack={() => setIsBooking(false)}
            />
          ) : (
            <PalPopupContent
              pal={currentPal}
              onSelectItem={(item) => setSelectedItem(item)}
              onDeleteItem={(id) => deleteItemMutation(id)}
              onEditItem={(item) => setEditingItem(item)}
              onAddItem={() => setIsAddingItem(true)}
            />
          )}
        </div>

        {isEditing && (
          <AddOrEditPalForm
            isOpen={true}
            onClose={() => setIsEditing(false)}
            onSubmitSuccess={(updatedPal) => {
              if (updatedPal) {
                setCurrentPal(updatedPal);
              }
              setIsEditing(false);
            }}
            palType={currentPal.type}
            isEdit={true}
            palToEdit={currentPal}
          />
        )}

        {editingItem && (
          <EditGearItemModal
            isOpen={true}
            onClose={() => setEditingItem(null)}
            item={editingItem}
          />
        )}

        {isAddingItem && currentPal.type === "GearPal" && (
          <AddGearItemModal
            isOpen={true}
            onClose={() => setIsAddingItem(false)}
            gearpalId={currentPal.id as number}
          />
        )}
      </div>
    </div>
  );
};

export default PalPopup;
